import React from 'react';

export const UserTerms = () => {
  return (
    <div className="prose max-w-none text-sm">
      <h2 className="text-xl font-bold text-gray-900 mb-4">EnayaHub User Terms & Conditions</h2>
      
      <p className="text-gray-600 mb-4">
        <strong>Effective Date:</strong> December 1, 2025<br />
        <strong>Governing Law:</strong> State of Kuwait<br />
        <strong>Company:</strong> Order for Marketing Products LLC (operating as EnayaHub)<br />
        <strong>Contact:</strong> support@enayahub.net
      </p>

      <div className="space-y-4">
        <section>
          <h3 className="font-semibold text-gray-900 mb-2">1. Acceptance of Terms</h3>
          <p className="text-gray-700">
            By accessing or using EnayaHub's website or mobile app ('Platform'), you agree to these Terms & Conditions ('Terms'). 
            If you do not agree, you may not use the Platform. Continued use after updates implies acceptance.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">2. Eligibility and Account Registration</h3>
          <p className="text-gray-700">
            You must be at least 18 years old to use the Platform. You agree to provide accurate information and safeguard your login credentials.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">3. Platform Purpose</h3>
          <p className="text-gray-700">
            EnayaHub acts as a booking and payment intermediary between Users and Providers. It does not provide medical or wellness services directly.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">4. Booking, Rescheduling, and Cancellation</h3>
          <p className="text-gray-700">
            Users may reschedule appointments up to two (2) times, at least three (3) hours before the scheduled time, 
            and may cancel appointments up to twenty-four (24) hours prior. Later changes must be handled directly with the Provider.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">5. Payments and Fees</h3>
          <p className="text-gray-700">
            All payments are processed through EnayaHub on behalf of Providers. Payment confirmation authorizes EnayaHub to remit funds 
            to the Provider after deducting fees. Refunds, when applicable, follow the Provider's policy.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">6. User Conduct</h3>
          <p className="text-gray-700">
            You agree not to use the Platform for illegal or fraudulent purposes, and not to solicit Providers outside EnayaHub. 
            Violations may result in account termination.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">7. Platform Responsibilities</h3>
          <p className="text-gray-700">
            EnayaHub strives to maintain secure, reliable services but does not guarantee uninterrupted operation or availability.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">8. Limitation of Liability</h3>
          <p className="text-gray-700">
            EnayaHub is not liable for Provider performance or service outcomes. It acts solely as an intermediary between Users and Providers.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">9. Suspension and Termination</h3>
          <p className="text-gray-700">
            EnayaHub may suspend or terminate accounts for violations, fraud, or abuse of booking privileges.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">10. Dispute Resolution</h3>
          <p className="text-gray-700">
            Disputes are governed by Kuwaiti law and subject to the exclusive jurisdiction of Kuwait City courts.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">11. Intellectual Property</h3>
          <p className="text-gray-700">
            All EnayaHub trademarks, logos, and content remain property of Order for Marketing Products LLC.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">12. Privacy Policy</h3>
          <p className="text-gray-700">
            EnayaHub collects personal data, including name, contact details, booking history, and payment data, to provide services. 
            Information is shared only with Providers, payment processors, or legal authorities when required. Data is securely stored 
            and may be retained as necessary. Users may request data access or deletion by contacting support@enayahub.net.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">13. Amendments</h3>
          <p className="text-gray-700">
            EnayaHub may update these Terms or Privacy Policy at any time. Updated versions take effect upon posting to the Platform.
          </p>
        </section>
      </div>

      <p className="text-gray-600 mt-6 text-xs">
        © 2025 Order for Marketing Products LLC — Effective December 1, 2025 — support@enayahub.net — Kuwait
      </p>
    </div>
  );
};

export const ProviderTerms = () => {
  return (
    <div className="prose max-w-none text-sm">
      <h2 className="text-xl font-bold text-gray-900 mb-4">EnayaHub Provider Terms & Conditions</h2>
      
      <p className="text-gray-600 mb-4">
        <strong>Effective Date:</strong> December 1, 2025<br />
        <strong>Governing Law:</strong> State of Kuwait<br />
        <strong>Company:</strong> Order for Marketing Products LLC (operating as EnayaHub)<br />
        <strong>Contact:</strong> support@enayahub.net
      </p>

      <div className="space-y-4">
        <section>
          <h3 className="font-semibold text-gray-900 mb-2">1. Acceptance of Terms</h3>
          <p className="text-gray-700">
            By registering as a Provider on the EnayaHub Platform ('Platform'), you agree to comply with these Terms & Conditions ('Terms'). 
            These Terms constitute a legally binding agreement between you and Order for Marketing Products LLC, operating as EnayaHub.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">2. Eligibility and Registration</h3>
          <p className="text-gray-700">
            Only licensed businesses or professionals operating in compliance with Kuwaiti law may register as Providers. 
            Providers must submit accurate, complete, and up-to-date information.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">3. Relationship Between EnayaHub and Providers</h3>
          <p className="text-gray-700">
            EnayaHub acts solely as a booking and payment intermediary between Users and Providers. 
            Providers operate as independent entities responsible for their own operations, pricing, and service quality.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">4. Listings and Service Obligations</h3>
          <p className="text-gray-700">
            Providers must ensure that all listings, pricing, and availability displayed on the Platform are accurate and updated. 
            Providers are solely responsible for the quality, legality, and safety of their services.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">5. Appointment Management</h3>
          <p className="text-gray-700">
            Users may reschedule appointments up to two (2) times if the request is made at least three (3) hours prior. 
            Users may cancel appointments up to twenty-four (24) hours in advance.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">6. Payments, Fees, and Commissions</h3>
          <p className="text-gray-700">
            All payments from Users are processed through EnayaHub on behalf of Providers. EnayaHub deducts commissions and processing fees 
            prior to remittance. Providers are responsible for applicable taxes or levies.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">7. Provider Conduct and Compliance</h3>
          <p className="text-gray-700">
            Providers shall not attempt to solicit Users outside the EnayaHub Platform or engage in deceptive practices. 
            Violation may result in termination, forfeiture of payments, and legal action.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">8. Termination and Suspension</h3>
          <p className="text-gray-700">
            EnayaHub reserves the right to suspend or terminate a Provider's account for violations, misconduct, or reputation damage.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">9. Intellectual Property and Branding</h3>
          <p className="text-gray-700">
            Providers grant EnayaHub a non-exclusive license to display their business name, logo, and content for promotional use.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">10. Limitation of Liability</h3>
          <p className="text-gray-700">
            EnayaHub provides the Platform as-is and is not liable for service outcomes or disputes.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">11. Confidentiality</h3>
          <p className="text-gray-700">
            Providers must keep all User data confidential and may use it only for appointment-related purposes through the Platform.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">12. Privacy Policy</h3>
          <p className="text-gray-700">
            EnayaHub collects Provider business and payment information to facilitate bookings and compliance. 
            Data may be shared with Users for confirmed bookings, payment processors, or legal authorities if required.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">13. Dispute Resolution</h3>
          <p className="text-gray-700">
            All disputes shall be governed by Kuwaiti law and resolved exclusively by the courts of Kuwait City.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">14. Amendments</h3>
          <p className="text-gray-700">
            EnayaHub may update these Terms or Privacy Policy at any time. Updates are effective upon posting to the Platform.
          </p>
        </section>
      </div>

      <p className="text-gray-600 mt-6 text-xs">
        © 2025 Order for Marketing Products LLC — Effective December 1, 2025 — support@enayahub.net — Kuwait
      </p>
    </div>
  );
};

export const DoctorTerms = () => {
  return (
    <div className="prose max-w-none text-sm">
      <h2 className="text-xl font-bold text-gray-900 mb-4">EnayaHub Doctor Terms & Conditions</h2>
      
      <p className="text-gray-600 mb-4">
        <strong>Effective Date:</strong> December 1, 2025<br />
        <strong>Governing Law:</strong> State of Kuwait<br />
        <strong>Company:</strong> Order for Marketing Products LLC (operating as EnayaHub)<br />
        <strong>Contact:</strong> support@enayahub.net
      </p>

      <div className="space-y-4">
        <section>
          <h3 className="font-semibold text-gray-900 mb-2">1. Acceptance of Terms</h3>
          <p className="text-gray-700">
            By registering as a Doctor on the EnayaHub Platform, you agree to comply with these Terms & Conditions. 
            These Terms constitute a legally binding agreement between you and Order for Marketing Products LLC, operating as EnayaHub.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">2. Eligibility and Licensing</h3>
          <p className="text-gray-700">
            Only qualified, licensed medical professionals authorized to practice in the State of Kuwait may register as Doctors.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">3. Role of EnayaHub</h3>
          <p className="text-gray-700">
            EnayaHub operates as a technology intermediary facilitating communication, scheduling, and payments between Doctors and Users. 
            EnayaHub does not provide medical advice or diagnosis.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">4. Professional Responsibilities</h3>
          <p className="text-gray-700">
            Doctors must comply with all laws, regulations, and ethical standards governing medical practice in Kuwait.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">5. Appointments, Cancellations, and Rescheduling</h3>
          <p className="text-gray-700">
            Users may reschedule or cancel appointments within set timeframes. Doctors may approve or deny late requests at their discretion.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">6. Payments and Fees</h3>
          <p className="text-gray-700">
            All payments are processed by EnayaHub on behalf of the Doctor. EnayaHub deducts commissions and fees before remittance.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">7. Confidentiality and Medical Data</h3>
          <p className="text-gray-700">
            Doctors must maintain strict user confidentiality in accordance with Kuwaiti medical ethics.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">8. Data Storage and Sharing</h3>
          <p className="text-gray-700">
            Medical and personal data may be stored in encrypted form. Access is limited to the Doctor, user, and authorized EnayaHub personnel.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">9. Teleconsultations and Digital Communication</h3>
          <p className="text-gray-700">
            All teleconsultations must be conducted through EnayaHub's secure system.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">10. Prohibited Conduct</h3>
          <p className="text-gray-700">
            Doctors must not solicit Users outside the Platform or share user data for marketing.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">11. Suspension and Termination</h3>
          <p className="text-gray-700">
            EnayaHub may suspend or terminate a Doctor's account for misconduct or violations of law.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">12. Limitation of Liability</h3>
          <p className="text-gray-700">
            EnayaHub is not liable for medical outcomes or treatment results. Doctors bear full responsibility for their professional conduct.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">13. Intellectual Property</h3>
          <p className="text-gray-700">
            All Platform content and branding belong to Order for Marketing Products LLC.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">14. Privacy Policy</h3>
          <p className="text-gray-700">
            EnayaHub collects Doctor credentials, contact, and transaction data for compliance and payment processing. 
            All medical data is encrypted and retained per Kuwaiti standards.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">15. Governing Law and Dispute Resolution</h3>
          <p className="text-gray-700">
            These Terms are governed by Kuwaiti law, and disputes are resolved exclusively by the courts of Kuwait City.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-gray-900 mb-2">16. Amendments</h3>
          <p className="text-gray-700">
            EnayaHub may modify these Terms or the Privacy Policy at any time. Updates take effect upon posting on the Platform.
          </p>
        </section>
      </div>

      <p className="text-gray-600 mt-6 text-xs">
        © 2025 Order for Marketing Products LLC — Effective December 1, 2025 — support@enayahub.net — Kuwait
      </p>
    </div>
  );
};
